import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ContractFieldChangeResponse } from '@interfaces/contracts/responses/contract-field-change.response';
import { PartnerContractResponse } from '@interfaces/partners/contracts/responses/partner-contract.response';
import {
  PartnerContractRevisionListItemResponse,
  PartnerContractRevisionResponse,
} from '@interfaces/partners/contracts/responses/partner-contract-revision.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PartnerContractsHttpService } from '@services/http/partners/partner-contracts-http.service';
import { saveBlobAsFile } from '@src/app/core/utils/zip-download.utils';
import { RichTextVariable } from '@src/app/shared/components/rich-text-editor/rich-text-editor.models';
import { Observable, catchError, finalize, forkJoin, map, merge, of, switchMap, tap } from 'rxjs';
import {
  createPartnerContractPartyForm,
  partnerContractPartyFormValue,
  readPartnerContractParty,
} from '../../components/partner-contract-party-form/partner-contract-party-form';
import {
  isPartnerContractFinalized,
  partnerContractNumber,
} from '../../utils/partner-contract.utils';

/** Ação em andamento, para o botão certo mostrar "carregando". */
export type PartnerContractBusyAction =
  | 'save'
  | 'refill'
  | 'finalize'
  | 'reopen'
  | 'delete'
  | 'pdf';

/**
 * Estado do editor da minuta do Contrato de Parceria Comercial (card 3462): a minuta carregada, os
 * formulários (título, corpo, dados da parte), a régua de "alterado" e as chamadas à API. O
 * componente fica só com confirmações, permissões e navegação.
 *
 * Texto travado (card 3643): o centro da tela mostra o texto **renderizado** que o backend devolve
 * (`renderedBodyControl`, só leitura). Só quem está na lista de editores do texto (`canEditText`)
 * recebe o corpo-fonte com os tokens (`sourceBodyControl`) e pode alterá-lo; os demais mudam o texto
 * só pelos campos da parte, e o backend renderiza de novo a cada gravação.
 *
 * Provido no componente (uma instância por tela aberta).
 */
@Injectable()
export class PartnerContractEditorState {
  private readonly contractsService = inject(PartnerContractsHttpService);
  private readonly dateFormatter = inject(DateFormatterService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  readonly titleControl = new FormControl<string>('', { nonNullable: true });
  /** Texto renderizado gravado (prévia no centro da tela). Nunca é editado nem enviado. */
  readonly renderedBodyControl = new FormControl<string>('', { nonNullable: true });
  /** Corpo-fonte com os tokens `{{chave}}`; só existe (e só é enviado) para editor do texto. */
  readonly sourceBodyControl = new FormControl<string>('', { nonNullable: true });
  readonly partyForm: FormGroup = createPartnerContractPartyForm(this.fb);

  readonly contract = signal<PartnerContractResponse | null>(null);
  readonly variables = signal<RichTextVariable[]>([]);
  readonly loading = signal(true);
  readonly busy = signal<PartnerContractBusyAction | null>(null);
  readonly dirty = signal(false);
  /** Variáveis que ainda aparecem no texto (vem do editor a cada mudança). */
  readonly unfilledKeys = signal<string[]>([]);

  /** Histórico de revisões (MG4), mais recente primeiro. Só é buscado depois que o painel abre. */
  readonly revisions = signal<PartnerContractRevisionListItemResponse[]>([]);
  /** Alterações de valores (card 3643), mais recente primeiro; buscadas junto com as revisões. */
  readonly fieldChanges = signal<ContractFieldChangeResponse[]>([]);
  readonly revisionsLoading = signal(false);
  private readonly revisionsRequested = signal(false);

  readonly isFinalized = computed(() => isPartnerContractFinalized(this.contract()?.status));
  /** O usuário está na lista de editores do texto (Parametrização › Comercial). */
  readonly canEditText = computed(() => !!this.contract()?.canEditText);
  /** "Editar texto" ligado: o centro troca a prévia pelo editor do corpo-fonte. */
  readonly textMode = signal(false);

  constructor() {
    // Toda resposta do servidor (salvar, preencher, finalizar, reabrir) pode ter criado revisão:
    // com o painel já aberto uma vez, a lista acompanha a minuta.
    effect(() => {
      const contract = this.contract();
      if (contract && this.revisionsRequested()) untracked(() => this.loadRevisions());
    });

    // A prévia (`renderedBodyControl`) fica fora: ela só recebe o que o servidor devolve.
    merge(
      this.titleControl.valueChanges,
      this.sourceBodyControl.valueChanges,
      this.partyForm.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.dirty.set(
          this.titleControl.dirty || this.sourceBodyControl.dirty || this.partyForm.dirty
        )
      );

    this.contractsService
      .getVariables()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: list => this.variables.set(list), error: () => this.variables.set([]) });
  }

  load(id: string): Observable<PartnerContractResponse | null> {
    this.loading.set(true);
    return this.contractsService.getContract(id).pipe(
      tap(contract => {
        if (contract) this.apply(contract);
      }),
      finalize(() => this.loading.set(false))
    );
  }

  /** Os formulários estão válidos para salvar (título, razão social, data e e-mail). */
  isValid(): boolean {
    // Travado (finalizado/sem permissão de editar), o grupo fica `DISABLED` e `valid` é falso:
    // nada vai ser gravado, então não há o que validar.
    if (this.partyForm.disabled) return true;
    return !!this.titleControl.value.trim() && this.partyForm.valid;
  }

  save(): Observable<PartnerContractResponse | null> {
    if (!this.contract()) return of(null);

    this.busy.set('save');
    return this.saveRequest().pipe(finalize(() => this.busy.set(null)));
  }

  /** Salva (se houver mudança) e substitui as variáveis restantes com os dados gravados. */
  refill(): Observable<PartnerContractResponse | null> {
    const contract = this.contract();
    if (!contract) return of(null);

    // Um único `busy`/`finalize` para o par PUT + POST: com o `save()` aqui dentro, o `finalize` dele
    // limpava o "carregando" no meio e o botão voltava a aceitar clique durante o refill.
    this.busy.set('refill');
    const saveFirst = this.dirty() ? this.saveRequest() : of(contract);
    return saveFirst.pipe(
      switchMap(() => this.contractsService.refillVariables(contract.id)),
      tap(updated => {
        if (updated) this.apply(updated);
      }),
      finalize(() => this.busy.set(null))
    );
  }

  finalize(): Observable<PartnerContractResponse | null> {
    return this.runStatusChange('finalize', id => this.contractsService.finalizeContract(id));
  }

  reopen(): Observable<PartnerContractResponse | null> {
    return this.runStatusChange('reopen', id => this.contractsService.reopenContract(id));
  }

  delete(): Observable<boolean> {
    const contract = this.contract();
    if (!contract) return of(false);

    this.busy.set('delete');
    return this.contractsService.deleteContract(contract.id).pipe(
      map(() => true),
      finalize(() => this.busy.set(null))
    );
  }

  /** Baixa o PDF do texto **gravado**. Quem chama decide se salva antes. */
  downloadPdf(): Observable<void> {
    const contract = this.contract();
    if (!contract) return of(undefined);

    this.busy.set('pdf');
    return this.contractsService
      .downloadContractPdf(contract.id, `${partnerContractNumber(contract)}.pdf`)
      .pipe(
        map(file => saveBlobAsFile(this.document, file.blob, file.fileName)),
        finalize(() => this.busy.set(null))
      );
  }

  /** Finalizar/Reabrir: salva antes se houver mudança, para não travar um texto desatualizado. */
  private runStatusChange(
    action: 'finalize' | 'reopen',
    request: (id: string) => Observable<PartnerContractResponse | null>
  ): Observable<PartnerContractResponse | null> {
    const contract = this.contract();
    if (!contract) return of(null);

    this.busy.set(action);
    const saveFirst = action === 'finalize' && this.dirty() ? this.saveRequest() : of(contract);
    return saveFirst.pipe(
      switchMap(() => request(contract.id)),
      tap(updated => {
        if (updated) this.apply(updated);
      }),
      finalize(() => this.busy.set(null))
    );
  }

  /**
   * PUT da minuta, sem mexer no `busy` (quem chama cuida). Guarda o que foi enviado: se a pessoa
   * continuou digitando enquanto a resposta não chegava, a resposta NÃO sobrescreve o editor (o texto
   * novo sumiria em silêncio, com o "alterado" zerado e o desfazer apagado); só a minuta e as
   * pendências são atualizadas, e a tela continua marcada como alterada.
   */
  private saveRequest(): Observable<PartnerContractResponse | null> {
    const contract = this.contract();
    if (!contract) return of(null);

    const sent = this.snapshot();
    // Corpo-fonte só de quem é editor do texto e só quando mudou: quem não é editor nunca manda
    // corpo (o backend recusaria um corpo-fonte diferente do atual).
    const sendSource = this.canEditText() && this.sourceBodyControl.dirty;
    return this.contractsService
      .updateContract(contract.id, {
        title: this.titleControl.value.trim(),
        ...readPartnerContractParty(this.partyForm, this.dateFormatter),
        ...(sendSource ? { sourceBodyHtml: this.sourceBodyControl.value } : {}),
      })
      .pipe(
        tap(updated => {
          const saved = updated ?? contract;
          if (this.snapshot() === sent) {
            // Nada mudou desde o envio: o corpo-fonte no editor já é o que foi gravado (o servidor
            // só sanitiza). Não regravar preserva o cursor e o histórico de desfazer.
            this.apply(saved, { keepSource: true });
          } else {
            this.contract.set(saved);
            this.unfilledKeys.set(saved.unfilledVariables ?? []);
            this.renderedBodyControl.setValue(saved.bodyHtml ?? '', { emitEvent: false });
          }
        })
      );
  }

  /** Pede o histórico de revisões (o painel chama ao abrir). */
  requestRevisions(): void {
    if (this.revisionsRequested()) {
      this.loadRevisions();
      return;
    }
    this.revisionsRequested.set(true); // o effect carrega
  }

  getRevision(revisionId: string): Observable<PartnerContractRevisionResponse | null> {
    const contract = this.contract();
    if (!contract) return of(null);
    return this.contractsService.getContractRevision(contract.id, revisionId);
  }

  /**
   * "Usar este texto" (só editor do texto): título e corpo-fonte da revisão (`sourceBodyHtml`, com as
   * variáveis) vão para o editor e a tela fica alterada. Nada é gravado aqui; a pessoa revisa e salva.
   * Sem `sourceBodyHtml` não faz nada: o texto renderizado apagaria as variáveis (revisão do card 3643).
   */
  useRevisionText(revision: PartnerContractRevisionResponse): void {
    if (!this.canEditText() || !revision.sourceBodyHtml) return;
    this.titleControl.setValue(revision.title ?? '');
    this.sourceBodyControl.setValue(revision.sourceBodyHtml);
    this.titleControl.markAsDirty();
    this.sourceBodyControl.markAsDirty();
    this.textMode.set(true);
    this.dirty.set(true);
  }

  /** Liga/desliga "Editar texto" (só editor do texto). O que foi digitado continua até salvar. */
  toggleTextMode(): void {
    if (!this.canEditText()) return;
    this.textMode.update(on => !on);
  }

  private loadRevisions(): void {
    const contract = this.contract();
    if (!contract) return;

    this.revisionsLoading.set(true);
    // As alterações de valores vêm junto; falhar nelas não pode esconder as revisões.
    forkJoin({
      revisions: this.contractsService.getContractRevisions(contract.id),
      fieldChanges: this.contractsService
        .getContractFieldChanges(contract.id)
        .pipe(catchError(() => of([] as ContractFieldChangeResponse[]))),
    })
      .pipe(
        finalize(() => this.revisionsLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ revisions, fieldChanges }) => {
          this.revisions.set([...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber));
          this.fieldChanges.set(
            [...fieldChanges].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
          );
        },
        error: () => {
          this.revisions.set([]);
          this.fieldChanges.set([]);
        },
      });
  }

  /** Foto dos campos editáveis, para saber se algo mudou durante um salvamento. */
  private snapshot(): string {
    return JSON.stringify([
      this.titleControl.value,
      this.sourceBodyControl.value,
      this.partyForm.getRawValue(),
    ]);
  }

  /**
   * Põe a minuta nos formulários sem marcar "alterado". A prévia sempre recebe o texto renderizado
   * novo; o corpo-fonte só é regravado sem `keepSource` (e só vem para editor do texto).
   */
  private apply(contract: PartnerContractResponse, options: { keepSource?: boolean } = {}): void {
    this.contract.set(contract);
    this.unfilledKeys.set(contract.unfilledVariables ?? []);
    if (!contract.canEditText || isPartnerContractFinalized(contract.status)) {
      this.textMode.set(false);
    }

    this.titleControl.setValue(contract.title ?? '', { emitEvent: false });
    this.renderedBodyControl.setValue(contract.bodyHtml ?? '', { emitEvent: false });
    if (!options.keepSource) {
      this.sourceBodyControl.setValue(contract.sourceBodyHtml ?? '', { emitEvent: false });
    }
    this.partyForm.reset(
      partnerContractPartyFormValue({
        partyType: contract.partyType,
        partyLegalName: contract.partyLegalName,
        partyShortName: contract.partyShortName,
        partyCnpj: contract.partyCnpj,
        partyCpf: contract.partyCpf,
        partyAddress: contract.partyAddress,
        partyRepresentativeName: contract.partyRepresentativeName,
        partyRepresentativeEmail: contract.partyRepresentativeEmail,
        contractDate: contract.contractDate,
        commissionGeneralRate: contract.commissionGeneralRate,
        commissionApprovedCreditRate: contract.commissionApprovedCreditRate,
      }),
      { emitEvent: false }
    );

    this.titleControl.markAsPristine();
    this.renderedBodyControl.markAsPristine();
    this.sourceBodyControl.markAsPristine();
    this.partyForm.markAsPristine();
    this.dirty.set(false);
  }
}
